<template>
    <div class="empty-state">
        <NResult :status="status || 'error'" :title="title" :description="description" size="small">
            <template #icon>
                <NIcon size="64" :color="iconColor">
                    <component :is="iconComponent" />
                </NIcon>
            </template>
            <template v-if="hasActiveFilters" #default>
                <NSpace vertical :size="12" align="center" style="margin-top: 16px;">
                    <NText depth="3" style="font-size: 14px;">{{ $t("support.filters.activeFilters") }}:</NText>
                    <NSpace :size="8" justify="center" wrap>
                        <NTag v-if="activeFilters?.search" type="info" closable @close="clearFilter('search')">
                            {{ $t("support.filters.search") }}: {{ activeFilters.search }}
                        </NTag>
                        <NTag v-if="activeFilters?.status" type="info" closable @close="clearFilter('status')">
                            {{ $t("support.filters.status") }}: {{ formatStatus(activeFilters.status) }}
                        </NTag>
                        <NTag v-if="activeFilters?.priority" type="info" closable @close="clearFilter('priority')">
                            {{ $t("support.filters.priority") }}: {{ formatPriority(activeFilters.priority) }}
                        </NTag>
                        <NTag v-if="activeFilters?.categories && activeFilters.categories.length > 0" type="info" closable @close="clearFilter('categories')">
                            {{ $t("support.filters.category") }}: {{ activeFilters.categories.length }}
                        </NTag>
                    </NSpace>
                </NSpace>
            </template>
            <template #footer>
                <NSpace v-if="actionText || showRetry || showContact" :size="12" justify="center">
                    <NButton v-if="actionText && actionHandler" type="primary" @click="actionHandler">
                        {{ actionText }}
                    </NButton>
                    <NButton v-if="showRetry" type="primary" @click="handleRetry" class="ltr">
                        <template #icon>
                            <component :is="RefreshIcon" />
                        </template>
                        {{ $t("support.errors.tryAgain") }}
                    </NButton>
                    <NButton v-if="showContact" quaternary @click="handleContact">
                        <template #icon>
                            <component :is="ContactIcon" />
                        </template>
                        {{ $t("support.errors.contactSupport") }}
                    </NButton>
                </NSpace>
            </template>
        </NResult>
    </div>
</template>

<script setup lang="ts">
import { computed, h } from 'vue';
import { NResult, NButton, NIcon, NSpace, NTag, NText } from 'naive-ui';
import { $t } from '@/locales';

const RefreshIcon = () => h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    width: '16',
    height: '16',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('polyline', { points: '23,4 23,10 17,10' }),
    h('polyline', { points: '1,20 1,14 7,14' }),
    h('path', { d: 'M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15' })
]);

const ContactIcon = () => h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    width: '16',
    height: '16',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('path', { d: 'M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z' }),
    h('polyline', { points: '22,6 12,13 2,6' })
]);
// Simple SVG icon components
const InboxIcon = () => h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    width: '64',
    height: '64',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('polyline', { points: '22,12 18,12 15,21 9,21 6,12 2,12' }),
    h('path', { d: 'M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z' })
]);

const ChatIcon = () => h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    width: '64',
    height: '64',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('path', { d: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' })
]);

const DocumentIcon = () => h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    width: '64',
    height: '64',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('path', { d: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' }),
    h('polyline', { points: '14,2 14,8 20,8' }),
    h('line', { x1: '16', y1: '13', x2: '8', y2: '13' }),
    h('line', { x1: '16', y1: '17', x2: '8', y2: '17' }),
    h('polyline', { points: '10,9 9,9 8,9' })
]);

const ErrorIcon = () => h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    width: '64',
    height: '64',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('circle', { cx: '12', cy: '12', r: '10' }),
    h('line', { x1: '15', y1: '9', x2: '9', y2: '15' }),
    h('line', { x1: '9', y1: '9', x2: '15', y2: '15' })
]);

const NetworkErrorIcon = () => h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    width: '64',
    height: '64',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('path', { d: 'M1.42 9a16 16 0 0 1 21.16 0' }),
    h('path', { d: 'M5 12.55a11 11 0 0 1 14.08 0' }),
    h('path', { d: 'M8.53 16.11a6 6 0 0 1 6.95 0' }),
    h('line', { x1: '12', y1: '20', x2: '12.01', y2: '20' }),
    h('line', { x1: '2', y1: '2', x2: '22', y2: '22' })
]);

const ShieldIcon = () => h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    width: '64',
    height: '64',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('path', { d: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' }),
    h('line', { x1: '9', y1: '9', x2: '9.01', y2: '9' }),
    h('line', { x1: '15', y1: '9', x2: '15.01', y2: '9' }),
    h('path', { d: 'M8 13s1.5 2 4 2 4-2 4-2' })
]);

const ClockIcon = () => h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    width: '64',
    height: '64',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('circle', { cx: '12', cy: '12', r: '10' }),
    h('polyline', { points: '12,6 12,12 16,14' })
]);

const RateLimitIcon = () => h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    width: '64',
    height: '64',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('circle', { cx: '12', cy: '12', r: '10' }),
    h('polyline', { points: '12,6 12,12 16,14' }),
    h('path', { d: 'M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83' })
]);

const ServerIcon = () => h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    width: '64',
    height: '64',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('rect', { x: '2', y: '3', width: '20', height: '4', rx: '1' }),
    h('rect', { x: '2', y: '9', width: '20', height: '4', rx: '1' }),
    h('rect', { x: '2', y: '15', width: '20', height: '4', rx: '1' }),
    h('line', { x1: '6', y1: '5', x2: '6.01', y2: '5' }),
    h('line', { x1: '6', y1: '11', x2: '6.01', y2: '11' }),
    h('line', { x1: '6', y1: '17', x2: '6.01', y2: '17' })
]);

const ToolIcon = () => h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    width: '64',
    height: '64',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('path', { d: 'M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z' })
]);

const TicketIcon = () => h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    width: '64',
    height: '64',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('path', { d: 'M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v2z' }),
    h('line', { x1: '13', y1: '5', x2: '13', y2: '19' })
]);

interface Props {
    type: 'no-tickets' | 'no-replies' | 'no-attachments' | 'no-search-results' | 'no-results' | 'error' | 'network-error' | 'permission-error' | 'timeout-error' | 'server-error' | 'maintenance' | 'rate-limit-error';
    title?: string;
    description?: string;
    message?: string;
    actionText?: string;
    actionHandler?: () => void;
    showRetry?: boolean;
    showContact?: boolean;
    errorCode?: string | number;
    activeFilters?: {
        search?: string;
        status?: string;
        priority?: string;
        categories?: string[];
    };
}

const props = defineProps<Props>();

const emit = defineEmits<{
    clearFilter: [filterName: string];
}>();

const hasActiveFilters = computed(() => {
    if (!props.activeFilters) return false;
    return !!(
        props.activeFilters.search ||
        props.activeFilters.status ||
        props.activeFilters.priority ||
        (props.activeFilters.categories && props.activeFilters.categories.length > 0)
    );
});

const formatStatus = (status: string) => {
    const statusMap: Record<string, string> = {
        'open': $t('support.status.open'),
        'in_progress': $t('support.status.inProgress'),
        'waiting_customer': $t('support.status.waitingCustomer'),
        'resolved': $t('support.status.resolved'),
        'closed': $t('support.status.closed')
    };
    return statusMap[status] || status;
};

const formatPriority = (priority: string) => {
    const priorityMap: Record<string, string> = {
        'low': $t('support.priority.low'),
        'medium': $t('support.priority.medium'),
        'high': $t('support.priority.high'),
        'critical': $t('support.priority.critical')
    };
    return priorityMap[priority] || priority;
};

const clearFilter = (filterName: string) => {
    emit('clearFilter', filterName);
};

const iconComponent = computed(() => {
    switch (props.type) {
        case 'no-tickets':
            return TicketIcon;
        case 'no-replies':
            return ChatIcon;
        case 'no-attachments':
            return DocumentIcon;
        case 'no-search-results':
        case 'no-results':
            return InboxIcon;
        case 'error':
            return ErrorIcon;
        case 'network-error':
            return NetworkErrorIcon;
        case 'permission-error':
            return ShieldIcon;
        case 'timeout-error':
            return ClockIcon;
        case 'rate-limit-error':
            return RateLimitIcon;
        case 'server-error':
            return ServerIcon;
        case 'maintenance':
            return ToolIcon;
        default:
            return InboxIcon;
    }
});

const status = computed(() => {
    switch (props.type) {
        case 'no-tickets':
        case 'no-search-results':
        case 'no-results':
            return '404';
        case 'no-replies':
        case 'no-attachments':
            return 'info';
        case 'error':
        case 'rate-limit-error':
            return 'warning';
        default:
            return 'info';
    }
});

const iconColor = computed(() => {
    switch (props.type) {
        case 'no-tickets':
        case 'no-search-results':
        case 'no-results':
            return 'var(--n-text-color-3)';
        case 'no-replies':
        case 'no-attachments':
            return 'var(--n-color-primary)';
        case 'error':
            return 'var(--n-error-color)';
        case 'rate-limit-error':
            return 'var(--n-warning-color)';
        default:
            return 'var(--n-text-color-3)';
    }
});

const title = computed(() => {
    if (props.title) return props.title;

    switch (props.type) {
        case 'no-tickets':
            return $t("support.emptyStates.noTickets.title");
        case 'no-replies':
            return $t("support.emptyStates.noReplies.title");
        case 'no-attachments':
            return $t("support.emptyStates.noAttachments.title");
        case 'no-search-results':
            return $t("common.noResults");
        case 'error':
            return $t("support.errors.genericError");
        case 'network-error':
            return $t("support.errors.networkError");
        case 'permission-error':
            return $t("support.errors.permissionError");
        case 'timeout-error':
            return $t("support.errors.genericError");
        case 'rate-limit-error':
            return $t('support.polling.tooManyRequests') || 'Rate Limit Exceeded';
        case 'server-error':
            return $t("support.errors.genericError");
        case 'maintenance':
            return $t("support.errors.genericError");
        default:
            return $t("common.noResults");
    }
});

const description = computed(() => {
    if (props.description) return props.description;
    if (props.message) return props.message;

    let baseMessage = '';

    switch (props.type) {
        case 'no-tickets':
            return $t("support.emptyStates.noTickets.description");
        case 'no-replies':
            return $t("support.emptyStates.noReplies.description");
        case 'no-attachments':
            return $t("support.emptyStates.noAttachments.description");
        case 'no-search-results':
            return $t("support.emptyStates.noSearchResults.description");
        case 'error':
            baseMessage = $t("support.errors.genericErrorDesc");
            break;
        case 'network-error':
            baseMessage = $t("support.errors.networkErrorDesc");
            break;
        case 'permission-error':
            baseMessage = $t("support.errors.permissionErrorDesc");
            break;
        case 'timeout-error':
            baseMessage = $t("support.errors.timeoutErrorDesc");
            break;
        case 'rate-limit-error':
            return $t('support.polling.tooManyRequestsDesc') || 'Too many requests. Please wait a moment and try again. We\'ve temporarily limited requests to ensure system stability.';
        case 'server-error':
            baseMessage = $t("support.errors.serverErrorDesc");
            break;
        case 'maintenance':
            return $t("support.errors.maintenanceErrorDesc");
        default:
            return $t("support.emptyStates.noData.description");
    }

    // Add error code if present
    if (props.errorCode && baseMessage) {
        return $t("support.errors.withErrorCode", { message: baseMessage, code: props.errorCode });
    }

    return baseMessage;
});

// Event handlers
const handleRetry = () => {
    if (props.actionHandler) {
        props.actionHandler();
    } else {
        // Default retry behavior - reload the page
        window.location.reload();
    }
};

const handleContact = () => {
    // Navigate to support contact or open email client
    const supportEmail = 'support@example.com';
    const subject = encodeURIComponent('Support Request - Error Report');
    const body = encodeURIComponent(`Error Type: ${props.type}\nError Code: ${props.errorCode || 'N/A'}\nTimestamp: ${new Date().toISOString()}\n\nDescription:\n`);
    window.location.href = `mailto:${supportEmail}?subject=${subject}&body=${body}`;
};
</script>

<style scoped>
.empty-state {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
    padding: 2rem;
}
</style>